import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useNavigate } from 'react-router-dom';

// Sample data - replace with actual data from your backend
const progressData = [
  { name: 'Week 1', progress: 20 },
  { name: 'Week 2', progress: 35 },
  { name: 'Week 3', progress: 50 },
  { name: 'Week 4', progress: 65 },
  { name: 'Week 5', progress: 80 },
  { name: 'Week 6', progress: 90 },
];

const stats = [
  {
    title: 'Current Streak',
    value: '5 days',
    icon: <TrendingUpIcon color="primary" fontSize="large" />,
    color: 'primary.main'
  },
  {
    title: 'Sessions Completed',
    value: '12/20',
    icon: <CheckCircleOutlineIcon color="success" fontSize="large" />,
    color: 'success.main'
  },
  {
    title: 'Time Spent',
    value: '8h 45m',
    icon: <AccessTimeIcon color="info" fontSize="large" />,
    color: 'info.main'
  },
  {
    title: 'Achievements',
    value: '3/10',
    icon: <EmojiEventsIcon color="warning" fontSize="large" />,
    color: 'warning.main'
  },
];

const Progress = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedTimeframe, setSelectedTimeframe] = useState('week');

  return (
    <Box sx={{
      p: isMobile ? 1 : 2, maxWidth: 1200,
      margin: '0 auto',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B2D6B' }}>
          My Progress Overview
        </Typography>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: '#F0F5FF',
            width: 35,
            height: 35,
            borderRadius: '50%',
            color: '#0B2D6B',
            '&:hover': {
              backgroundColor: '#E0EBFF'
            },
            mb: 3
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div" sx={{ color: stat.color, fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Progress Chart */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Progress Over Time
          </Typography>
          <Box>
            {['week', 'month', 'year'].map((timeframe) => (
              <Typography
                key={timeframe}
                component="span"
                sx={{
                  mx: 1,
                  cursor: 'pointer',
                  color: selectedTimeframe === timeframe ? 'primary.main' : 'text.secondary',
                  fontWeight: selectedTimeframe === timeframe ? 'bold' : 'normal',
                  '&:hover': { color: 'primary.main' }
                }}
                onClick={() => setSelectedTimeframe(timeframe)}
              >
                {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
              </Typography>
            ))}
          </Box>
        </Box>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value) => [`${value}%`, 'Progress']}
                labelFormatter={(label) => `Time: ${label}`}
              />
              <Line
                type="monotone"
                dataKey="progress"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

    </Box >
  );
};

export default Progress;
